import { BundleBuilderMixinComponent } from '@theme/bundle-builder-mixin';
import { formatMoney } from '@theme/money-formatting';
import { getDynamicBundleItems, fetchBundleSettings } from '@theme/bundle-builder-sdk';
import {
  buildDynamicBundle,
  buildDynamicBundleSelection,
  enrichDynamicBundleCartItems,
  postCartAddItems,
} from '@theme/bundle-builder-cart';
import { VariantUpdateEvent, CartAddEvent, CartErrorEvent } from '@theme/events';
import {
  focusSellingPlanSelect,
  getSelectValueOrFirst,
  normalizePurchaseType,
} from '@theme/bundle-builder-purchase-types-utils';
import { getViewParameterValue, yieldToMainThread } from '@theme/utilities';
import { resolveListContext, normalizeShopifyResourceId } from '@theme/gtm-events';

/**
 * Updates the `variant` query param in the URL without a full page reload.
 *
 * @param {string | null | undefined} variantId
 * @param {object} [options]
 * @param {string} [options.url] - Base URL to mutate (defaults to current location).
 * @param {string} [options.pathname] - Optional pathname override (combined listings).
 * @param {unknown} [options.historyState] - State passed to `history.replaceState`.
 */
function replaceVariantInUrl(variantId, options = {}) {
  const currentHref = options.url ?? window.location.href;
  const url = new URL(currentHref);

  if (variantId) {
    url.searchParams.set('variant', variantId);
  } else {
    url.searchParams.delete('variant');
  }

  if (options.pathname) {
    url.pathname = options.pathname;
  }

  if (url.href === currentHref) return;

  yieldToMainThread().then(() => {
    history.replaceState(options.historyState ?? {}, '', url.toString());
  });
}

/**
 * Builds a product section rendering URL for variant updates.
 *
 * @param {object} options
 * @param {string} options.productUrl
 * @param {string} [options.variantId]
 * @param {string} [options.sectionId]
 * @param {string[]} [options.extraParams] - Raw query param strings (e.g. `option_values=1,2`).
 * @returns {string | null}
 */
function buildProductSectionRequestUrl({ productUrl, variantId, sectionId, extraParams = [] }) {
  if (!productUrl) return null;

  /** @type {string[]} */
  const params = [...extraParams];

  const viewParamValue = getViewParameterValue();
  if (viewParamValue) params.push(`view=${viewParamValue}`);
  if (variantId) params.push(`variant=${variantId}`);
  if (sectionId) params.push(`section_id=${sectionId}`);

  const baseUrl = productUrl.split('?')[0];
  return `${baseUrl}?${params.join('&')}`;
}

/**
 * @param {string} responseText
 * @returns {Document}
 */
function parseProductSectionHtml(responseText) {
  const html = new DOMParser().parseFromString(responseText, 'text/html');
  html.querySelector('overflow-list[defer]')?.removeAttribute('defer');
  return html;
}

/**
 * Fetches a product section and parses embedded variant JSON.
 *
 * @param {string} requestUrl
 * @param {object} options
 * @param {string} options.jsonSelector - CSS selector for `script[type="application/json"]`.
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<{ html: Document, variantData: unknown } | null>}
 */
async function fetchProductSectionJson(requestUrl, { jsonSelector, signal }) {
  const response = await fetch(requestUrl, { signal });
  const responseText = await response.text();
  const html = parseProductSectionHtml(responseText);
  const textContent = html.querySelector(jsonSelector)?.textContent;

  if (!textContent) return null;

  return {
    html,
    variantData: JSON.parse(textContent),
  };
}

/** @typedef {import('./bundle-builder-mixin.js').BundleBuilderPropSpec} BundleBuilderPropSpec */
/** @typedef {import('./bundle-builder-sdk.js').RechargeDynamicBundle} RechargeDynamicBundle */
/** @typedef {import('./bundle-builder-sdk.js').RechargeDynamicBundleSelection} RechargeDynamicBundleSelection */
/** @typedef {{
 *   quantity: number,
 *   title: string,
 *   product_id?: number,
 *   variant_id?: number,
 *   collection_id?: number | null,
 *   variant: { id: string | number, price: number, compare_at_price?: number, product_id?: number, product?: { id: number } },
 * }} VariantPack */

class ProductCustomBundle extends BundleBuilderMixinComponent {
  requiredRefs = ['addToCartButton', 'addToCartTotal', 'addToCartLabel', 'addToCartError'];

  /** @type {Record<string, BundleBuilderPropSpec>} */
  props = {
    variantPacks: {
      type: 'array',
      required: true,
      attributeName: 'data-variant-packs',
    },
    moneyFormat: {
      type: 'string',
      required: true,
      attributeName: 'data-money-format',
    },
    currency: {
      type: 'string',
      required: true,
      attributeName: 'data-currency',
    },
  };

  /** @type {Array<{ id: string, title: string, sku: string, price: number, subscription_packs: Array<VariantPack>, one_time_packs: Array<VariantPack> }>} */
  variantPacks;

  /** @type {string} */
  moneyFormat;

  /** @type {string} */
  currency;

  /** @type {string | undefined} */
  selectedVariantId;

  /** @type {'subscribe' | 'one-time'} */
  selectedPurchaseType;

  /** @type {string | null} */
  selectedSellingPlanId = null;

  /** @type {import('./bundle-builder-sdk.js').CDNBundleSettings | null} */
  #bundleSettings = null;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {boolean} */
  #isAdding = false;

  get selectedVariantPacks() {
    return this.variantPacks.find((variant) => variant.id.toString() === this.selectedVariantId?.toString());
  }

  get selectedPacks() {
    return this.selectedPurchaseType === 'subscribe'
      ? this.selectedVariantPacks?.subscription_packs
      : this.selectedVariantPacks?.one_time_packs;
  }

  get activeCartTotal() {
    return this.#getPackTotalCents(this.selectedPacks);
  }

  get isAddToCartEnabled() {
    if (this.#isAdding) return false;
    if (this.selectedPurchaseType === 'one-time') {
      return Boolean(this.selectedVariantId && this.selectedPacks?.length);
    }
    return Boolean(this.#getDynamicBundle());
  }

  connectedCallback() {
    super.connectedCallback();
    this.selectedPurchaseType = 'subscribe';
    this.selectedVariantId = this.#getInitialVariantId();
    this.selectedSellingPlanId = this.#getInitialSellingPlanId();
    this.render();
    this.#loadBundleSettings();
  }

  /**
   * @param {Event} event
   */
  handleVariantSelection(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.checked) return;

    const variantId = target.dataset.variant;
    if (!variantId || variantId === this.selectedVariantId) return;

    this.selectedVariantId = variantId;
    this.render();

    if (this.dataset.templateProductMatch === 'true') {
      replaceVariantInUrl(variantId);
      this.#fetchUpdatedSection(variantId);
    }
  }

  /**
   * @param {Event} event
   */
  handlePurchaseTypeChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.checked) return;

    this.selectedPurchaseType = normalizePurchaseType(target.value);
    this.render();

    if (this.selectedPurchaseType === 'subscribe') {
      focusSellingPlanSelect(/** @type {HTMLSelectElement | undefined} */ (this.refs.sellingPlanSelect));
    }
  }

  /**
   * @param {Event} event
   */
  handleSellingPlanChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLSelectElement)) return;

    this.selectedSellingPlanId = target.value || null;
  }

  /**
   * @param {Event} event
   */
  async handleAddToCart(event) {
    event?.preventDefault?.();
    if (!this.isAddToCartEnabled) return;

    const button = /** @type {HTMLButtonElement} */ (this.refs.addToCartButton);

    this.#isAdding = true;
    this.#clearAddToCartError();
    this.#renderAddToCart();
    button.setAttribute('aria-busy', 'true');

    try {
      const { ok, response } =
        this.selectedPurchaseType === 'one-time'
          ? await this.#addOneTimePurchase()
          : await this.#addSubscribePurchase();

      if (!ok) {
        const failureMessage =
          response.message || 'Unable to add bundle to cart. Please try again.';
        this.#showAddToCartError(failureMessage);
        this.dispatchEvent(
          new CartErrorEvent(
            this.id,
            failureMessage,
            response.description ?? {},
            response.errors ?? {}
          )
        );
        return;
      }

      this.dispatchEvent(
        new CartAddEvent(response, this.id, {
          source: 'product-custom-bundle-add-to-cart',
          itemCount: 1,
          sections: response.sections ?? {},
        })
      );
    } catch (err) {
      const failureMessage =
        err instanceof Error ? err.message : 'Unable to add bundle to cart. Please try again.';
      this.#showAddToCartError(failureMessage);
      this.dispatchEvent(new CartErrorEvent(this.id, failureMessage, {}, err));
    } finally {
      this.#isAdding = false;
      button.removeAttribute('aria-busy');
      this.#renderAddToCart();
    }
  }

  /**
   * GTM / Custom Pixel line properties for checkout attribution.
   * @returns {Record<string, string>}
   */
  #getGtmLineProperties() {
    const packs = this.selectedPacks ?? [];
    const boxSize = packs.reduce((sum, pack) => sum + (Number(pack.quantity) || 0), 0);
    const boxCapacity =
      this.dataset.gtmBoxCapacity != null && this.dataset.gtmBoxCapacity !== ''
        ? Number(this.dataset.gtmBoxCapacity)
        : boxSize;
    const purchaseType = this.selectedPurchaseType === 'subscribe' ? 'subscription' : 'one_time';
    const deliveryFrequency = this.#resolveSellingPlanLabel();

    /** @type {Record<string, string>} */
    const props = {
      _purchase_type: purchaseType,
      _delivery_frequency: deliveryFrequency,
      _box_type: this.dataset.gtmBoxType || 'Variety Pack',
    };
    if (boxSize > 0) props._box_size = String(boxSize);
    if (boxCapacity > 0) props._box_capacity = String(boxCapacity);

    // Browse-list attribution for checkout Custom Pixel (same as standard PDP / BYB).
    // Fall back to page-level list (product handle, e.g. starter-box) when no prior list.
    const productId = this.dataset.productId;
    if (productId) {
      const ctx = resolveListContext(productId, {
        item_list_id: this.dataset.gtmListId || '',
        item_list_name: this.dataset.gtmListName || '',
        index: 1,
      });
      if (ctx.item_list_id) {
        props._item_list_id = ctx.item_list_id;
        props._item_list_name = ctx.item_list_name ?? '';
      }
      if (ctx.index != null) props._index = String(ctx.index);
      const gtmItemId = normalizeShopifyResourceId(productId);
      if (gtmItemId) props._gtm_item_id = gtmItemId;
    }

    return props;
  }

  /**
   * @returns {string}
   */
  #resolveSellingPlanLabel() {
    if (this.selectedPurchaseType !== 'subscribe' || !this.selectedSellingPlanId) return '';
    const select = /** @type {HTMLSelectElement | undefined} */ (this.refs.sellingPlanSelect);
    const option = select?.selectedOptions?.[0];
    return option?.textContent?.trim() ?? '';
  }

  /**
   * One-time path: adds only the shell variant to cart.
   * The Cart Transform Function (OUF-298) expands it into individual products at checkout.
   */
  async #addOneTimePurchase() {
    const variantId = this.selectedVariantId;
    if (!variantId) throw new Error('No variant selected.');

    const bundleId = Math.random().toString(36).slice(2, 10);

    return postCartAddItems([
      {
        id: Number(variantId),
        quantity: 1,
        properties: {
          _sb_bundle_title: this.#buildBundleTitle(),
          _sb_bundle_group: `${bundleId} (1/1)`,
          _sb_bundle_variant_id_qty: `${variantId}:1`,
          ...this.#getGtmLineProperties(),
        },
      },
    ]);
  }

  /**
   * Subscribe path: builds Recharge dynamic bundle and adds all component items to cart.
   */
  async #addSubscribePurchase() {
    const bundle = this.#getDynamicBundle();
    const shopifyProductHandle = this.dataset.bundleProductHandle;
    if (!bundle || !shopifyProductHandle) throw new Error('Bundle configuration missing.');

    const rcItems = await getDynamicBundleItems(bundle, shopifyProductHandle);
    const selectedPacks = this.selectedPacks;

    const itemsWithProps = enrichDynamicBundleCartItems(rcItems, {
      bundleTitle: this.#buildBundleTitle(),
      bundleImage: this.dataset.productFeaturedImage,
      getQuantity: (variantId) => {
        const pack = selectedPacks?.find(
          (packItem) => Number(packItem.variant_id ?? packItem.variant?.id) === variantId
        );
        return pack?.quantity;
      },
      bundleUrl: `${this.dataset.productUrl}${this.selectedVariantPacks?.id ? `?variant=${this.selectedVariantPacks?.id}` : ``}`,
      bundleVariantTitle: `${this.selectedVariantPacks?.title || ''}`,
      extraProperties: this.#getGtmLineProperties(),
    });

    for (const item of itemsWithProps) {
      delete item.properties['_sb_bundle_variant_id_qty'];
    }

    return postCartAddItems(itemsWithProps);
  }

  /**
   * Builds _sb_bundle_title: "{product title} | SKU: {variant sku}".
   * Title and SKU come from the shell PDP product, not the Build-A-Box product.
   *
   * @returns {string}
   */
  #buildBundleTitle() {
    const productTitle = this.dataset.productTitle || 'Bundle';
    const sku = this.selectedVariantPacks?.sku;
    return sku ? `${productTitle} | SKU: ${sku}` : productTitle;
  }

  render() {
    const subscribeTotal = this.#getPackTotalCents(this.selectedVariantPacks?.subscription_packs);
    const oneTimeTotal = this.#getPackTotalCents(this.selectedVariantPacks?.one_time_packs);

    this.renderObjectToHTML({
      subscribePrice: subscribeTotal > 0 ? formatMoney(subscribeTotal, this.moneyFormat, this.currency) : '',
      oneTimePrice: oneTimeTotal > 0 ? formatMoney(oneTimeTotal, this.moneyFormat, this.currency) : '',
      subscribeComparePrice:
        oneTimeTotal > subscribeTotal ? formatMoney(oneTimeTotal, this.moneyFormat, this.currency) : '',
      oneTimeComparePrice: '',
    });
    this.#renderPurchaseTypeState();
    this.#renderVariantSelection();
    this.#renderAddToCart();
  }

  /**
   * @param {VariantPack} pack
   * @returns {boolean}
   */
  #isPackCartable(pack) {
    const collectionId = pack.collection_id;
    const externalProductId = pack.product_id ?? pack.variant?.product_id ?? pack.variant?.product?.id;
    const externalVariantId = pack.variant_id ?? pack.variant?.id;

    return Boolean(collectionId && externalProductId && externalVariantId);
  }

  /**
   * @param {VariantPack} pack
   * @returns {RechargeDynamicBundleSelection | null}
   */
  #getPackSelection(pack) {
    if (!this.#isPackCartable(pack)) return null;

    return buildDynamicBundleSelection({
      collectionId: /** @type {string | number} */ (pack.collection_id),
      externalProductId:
        pack.product_id ?? pack.variant?.product_id ?? pack.variant?.product?.id ?? '',
      externalVariantId: pack.variant_id ?? pack.variant?.id ?? '',
      quantity: pack.quantity,
      sellingPlanId: this.selectedSellingPlanId,
      includeSellingPlan: this.selectedPurchaseType === 'subscribe',
    });
  }

  /** @param {number} totalQuantity @returns {string | null} */
  #resolveBundleExternalVariantId(totalQuantity) {
    const settings = this.#bundleSettings;
    if (!settings?.variants?.length) return null;

    for (const variant of settings.variants) {
      const range = variant.ranges?.[0];
      if (range && totalQuantity >= range.quantity_min && totalQuantity <= range.quantity_max) {
        return String(variant.external_variant_id);
      }
    }

    const fallback = settings.default_bundle_variant_id;
    return fallback != null ? String(fallback) : null;
  }

  /** @returns {RechargeDynamicBundle | null} */
  #getDynamicBundle() {
    const packs = this.selectedPacks;
    const babProductId = this.dataset.bundleProductId;

    if (!babProductId || !this.#bundleSettings || !packs?.length) return null;

    const totalQuantity = packs.reduce((sum, pack) => sum + pack.quantity, 0);
    const externalVariantId = this.#resolveBundleExternalVariantId(totalQuantity);
    if (!externalVariantId) return null;

    /** @type {RechargeDynamicBundleSelection[]} */
    const selections = [];

    for (const pack of packs) {
      const selection = this.#getPackSelection(pack);
      if (!selection) return null;
      selections.push(selection);
    }

    return buildDynamicBundle({
      externalProductId: babProductId,
      externalVariantId,
      selections,
    });
  }

  async #loadBundleSettings() {
    const babProductId = this.dataset.bundleProductId;
    if (!babProductId) return;

    try {
      this.#bundleSettings = await fetchBundleSettings(babProductId);
      this.#renderAddToCart();
    } catch (err) {
      console.error('Failed to load bundle settings:', err);
      this.#showAddToCartError('Unable to load subscription options. Please refresh and try again.');
    }
  }

  #clearAddToCartError() {
    const region = /** @type {HTMLElement | undefined} */ (this.refs.addToCartError);
    const messageEl = region?.querySelector('.bundle-builder__error-region-message');

    if (!region || !messageEl) return;

    messageEl.textContent = '';
    region.hidden = true;
  }

  /**
   * @param {string} message
   */
  #showAddToCartError(message) {
    const region = /** @type {HTMLElement | undefined} */ (this.refs.addToCartError);
    const messageEl = region?.querySelector('.bundle-builder__error-region-message');

    if (!region || !messageEl) return;

    messageEl.textContent = message;
    region.hidden = false;

    requestAnimationFrame(() => {
      region.focus();
    });
  }

  #renderAddToCart() {
    const button = /** @type {HTMLButtonElement | undefined} */ (this.refs.addToCartButton);
    const totalEl = /** @type {HTMLElement | undefined} */ (this.refs.addToCartTotal);
    const labelEl = /** @type {HTMLElement | undefined} */ (this.refs.addToCartLabel);

    if (!button || !totalEl || !labelEl) return;

    button.disabled = !this.isAddToCartEnabled;
    totalEl.textContent = formatMoney(this.activeCartTotal, this.moneyFormat, this.currency);
    labelEl.textContent = this.dataset.labelAddToCart ?? 'Add to cart';
    button.classList.toggle('is-loading', this.#isAdding);
  }

  /**
   * @param {Array<VariantPack> | undefined} packs
   * @returns {number}
   */
  #getPackTotalCents(packs) {
    return (packs ?? []).reduce((total, pack) => {
      if (!this.#isPackCartable(pack)) return total;
      const price = Number(pack.variant?.price ?? 0);
      return total + price * pack.quantity;
    }, 0);
  }

  #renderPurchaseTypeState() {
    const subscribeInput = /** @type {HTMLInputElement | undefined} */ (this.refs.subscribeInput);
    const oneTimeInput = /** @type {HTMLInputElement | undefined} */ (this.refs.oneTimeInput);
    const subscribeBody = /** @type {HTMLElement | undefined} */ (this.refs.subscribeBody);

    if (subscribeInput) {
      subscribeInput.checked = this.selectedPurchaseType === 'subscribe';
    }

    if (oneTimeInput) {
      oneTimeInput.checked = this.selectedPurchaseType === 'one-time';
    }

    if (subscribeBody) {
      subscribeBody.hidden = this.selectedPurchaseType !== 'subscribe';
    }
  }

  #renderVariantSelection() {
    for (const input of this.querySelectorAll('.product-custom-bundle__packs input[type="radio"]')) {
      if (!(input instanceof HTMLInputElement)) continue;
      input.checked = input.dataset.variant?.toString() === this.selectedVariantId?.toString();
    }
  }

  /**
   * @returns {string | undefined}
   */
  #getInitialVariantId() {
    const urlVariantId = new URL(window.location.href).searchParams.get('variant');
    if (urlVariantId && this.variantPacks.some((variant) => variant.id.toString() === urlVariantId)) {
      return urlVariantId;
    }

    return this.variantPacks[this.variantPacks.length - 1]?.id?.toString();
  }

  /**
   * @returns {string | null}
   */
  #getInitialSellingPlanId() {
    return getSelectValueOrFirst(/** @type {HTMLSelectElement | undefined} */ (this.refs.sellingPlanSelect));
  }

  /**
   * @param {string} variantId
   */
  #fetchUpdatedSection(variantId) {
    const requestUrl = buildProductSectionRequestUrl({
      productUrl: this.dataset.productUrl ?? '',
      variantId,
      sectionId: this.dataset.sectionId,
    });

    if (!requestUrl) return;

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetchProductSectionJson(requestUrl, {
      signal: this.#abortController.signal,
      jsonSelector: 'product-custom-bundle-component script[type="application/json"]',
    })
      .then((result) => {
        if (!result) return;

        this.dispatchEvent(
          new VariantUpdateEvent(
            /** @type {ConstructorParameters<typeof VariantUpdateEvent>[0]} */ (result.variantData),
            variantId,
            {
              html: result.html,
              productId: this.dataset.productId ?? '',
            }
          )
        );
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        console.error(error);
      });
  }
}

customElements.define('product-custom-bundle-component', ProductCustomBundle);
